import { type ReactNode, useEffect, useState } from 'react';

import { LoadingSkeleton } from '../../common/components/skeleton/skeleton';
import { useJiraAdminPermissions } from '../../controllers/use-jira-admin-permissions';

const JiraAccessRedirect = () => {
	useEffect(() => {
		// Let Jira handle its own access-denied or request-access experience.
		window.location.replace('/jira');
	}, []);

	return <LoadingSkeleton />;
};

export const JiraProductAccessBoundary = ({
	cloudId,
	children,
}: {
	cloudId: string;
	children: ReactNode;
}) => {
	const { getJiraAdminPermissions, permissions, loading, error } = useJiraAdminPermissions();
	const [hasCheckedAccess, setHasCheckedAccess] = useState(false);

	useEffect(() => {
		let active = true;
		getJiraAdminPermissions(cloudId).then(() => {
			if (active) {
				setHasCheckedAccess(true);
			}
		});
		return () => {
			active = false;
		};
	}, [cloudId, getJiraAdminPermissions]);

	if (!hasCheckedAccess || loading) {
		return <LoadingSkeleton />;
	}

	if (error) {
		throw new Error('Unable to verify Jira product access');
	}

	// Site-scoped write access or product administration grants Jira product access.
	if (!permissions.includes('write') && !permissions.includes('manage')) {
		return <JiraAccessRedirect />;
	}

	return <>{children}</>;
};
